import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import {
  Laptop,
  Smartphone,
  Package,
  Armchair,
  Printer,
  Edit,
  Printer as PrinterIcon,
  Trash2,
  User as UserIcon,
  MapPin,
  Wallet,
  AlertCircle,
  FileText,
  Download,
  UserPlus,
  UserMinus,
  ArrowRightLeft,
  ClipboardCheck,
  History,
  Wrench,
  Repeat,
  Archive,
} from 'lucide-react';
import { PageContainer } from '../../../shared/components/PageContainer';
import { Button } from '../../../shared/components/Button';
import { Alert } from '../../../shared/components/Alert';
import { locationTypeLabel } from '../../../shared/lib/locations';
import { AtivoFormModal } from '../components/AtivoFormModal';
import { AtivoAnexosTab } from '../components/AtivoAnexosTab';
import { AtivoStatusBadge } from '../components/AtivoStatusBadge';
import { ConfirmDialog } from '../../../shared/components/ConfirmDialog';
import { EntregaModal } from '../components/EntregaModal';
import { DevolucaoModal } from '../components/DevolucaoModal';
import { TransferenciaModal } from '../components/TransferenciaModal';
import { HistoricoResponsaveisDrawer } from '../components/HistoricoResponsaveisDrawer';
import { MovimentacoesTab } from '../components/MovimentacoesTab';
import { ManutencaoTab } from '../components/ManutencaoTab';
import { LoanModal } from '../components/LoanModal';
import { ReplaceModal } from '../components/ReplaceModal';
import { RetireModal } from '../components/RetireModal';
import { LicencasTab } from '../components/LicencasTab';
import { CardAlertas } from '../components/CardAlertas';
import { InventarioTab } from '../components/InventarioTab';
import { useAtivo, useExcluirAtivo, useDownloadDocumento, useAssignments, useAssinar } from '../hooks';
import { authService } from '../../../shared/lib/auth';
import { usePermissions, FEATURES } from '../../../shared/hooks/usePermissions';
import { formatarData, formatarMoeda, formatarTamanho, garantiaVencendo } from '../utils/formatters';
import type { AssetCategoryGroup } from '../api';

const TABS = [
  { key: 'movements', label: 'Histórico de Movimentações' },
  { key: 'maintenance', label: 'Manutenções' },
  { key: 'licenses', label: 'Licenças' },
  { key: 'inventory', label: 'Inventário' },
  { key: 'attachments', label: 'Anexos' },
] as const;

type TabKey = (typeof TABS)[number]['key'];

const SPEC_LABELS: Record<string, string> = {
  description: 'Informações adicionais',
  material: 'Material',
  color: 'Cor',
  structure: 'Estrutura',
  upholstery: 'Revestimento',
  backrest: 'Encosto',
  wheels: 'Rodízios',
  supportedWeight: 'Peso suportado',
  dimensions: 'Dimensões (L x A x P)',
};

function CategoryIcon({ group }: { group: AssetCategoryGroup | null | undefined }) {
  const cls = 'h-12 w-12 text-blue-500';
  if (group === 'it_equipment') return <Laptop className={cls} />;
  if (group === 'mobile_device') return <Smartphone className={cls} />;
  if (group === 'furniture') return <Armchair className={cls} />;
  if (group === 'software') return <Package className={cls} />;
  return <Printer className={cls} />;
}

export default function AtivoDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const detail = useAtivo(id);
  const excluir = useExcluirAtivo();
  const downloadMutation = useDownloadDocumento(id);
  const user = authService.getCurrentUser();
  const { can } = usePermissions();
  const canEdit = can(FEATURES.ativos, 'edit');
  const canDelete = can(FEATURES.ativos, 'delete');
  const canAssign = can(FEATURES.ativosResponsabilidade, 'edit');
  const canMaintain = can(FEATURES.ativosManutencao, 'edit');
  const canRetire = can(FEATURES.ativosBaixa, 'edit');
  const [tab, setTab] = useState<TabKey>('movements');
  // Um único slot de feedback por página. Success ganha Alert verde
  // (ex.: "Empréstimo registrado…"), error ganha Alert vermelho. Ambos
  // são limpos automaticamente quando o assignment ativo passa de
  // `pending` para `signed` (evita mensagem persistente após
  // assinatura). Também é limpo manualmente em fluxos que sabem que a
  // mensagem anterior ficou stale.
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  // SPEC 02
  const [entregaOpen, setEntregaOpen] = useState(false);
  const [devolucaoOpen, setDevolucaoOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [historicoOpen, setHistoricoOpen] = useState(false);
  // SPEC 03
  const [loanOpen, setLoanOpen] = useState(false);
  const [replaceOpen, setReplaceOpen] = useState(false);
  const [retireOpen, setRetireOpen] = useState(false);
  const assignmentsQuery = useAssignments(id);
  const assinarMutation = useAssinar(id);
  const tabsRef = useRef<HTMLElement | null>(null);

  // Muda de aba e rola até a section — sem isto, o botão "Manutenção"
  // no header parecia não fazer nada porque a tab bar ficava fora da
  // viewport (o usuário só via a mudança se scrollasse manualmente).
  const goToTab = (key: TabKey) => {
    setTab(key);
    // Aguarda 1 frame pra section renderizar com a nova aba ativa
    // antes de rolar (sem o `requestAnimationFrame` o scroll acontece
    // enquanto o React ainda está trocando o conteúdo).
    requestAnimationFrame(() => {
      tabsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  // Limpa mensagem "pendente de assinatura" assim que o backend confirma
  // que a assignment ativa foi assinada — sem isso, a mensagem persistia
  // pra sempre até refresh manual. Fica ANTES dos early returns pra
  // respeitar as Rules of Hooks (mesma ordem em todo render).
  const activeAssignment = assignmentsQuery.data?.data.find((a) => a.returnedAt === null);
  useEffect(() => {
    if (activeAssignment?.signatureStatus === 'signed' && feedback?.type === 'success') {
      setFeedback(null);
    }
  }, [activeAssignment?.signatureStatus, feedback?.type]);

  if (detail.isLoading) {
    return (
      <PageContainer title="Carregando…">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="lg:col-span-2 h-96 animate-pulse rounded bg-gray-100" />
          <div className="h-96 animate-pulse rounded bg-gray-100" />
        </div>
      </PageContainer>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <PageContainer title="Ativo não encontrado">
        <Alert variant="error">Não foi possível carregar este ativo.</Alert>
      </PageContainer>
    );
  }

  const d = detail.data;
  const asset = d.asset;
  const displayCode = asset.displayCode ?? asset.assetCode;
  const qrValue = `${window.location.origin}/ativos/${asset.id}`;
  const warrantyClass = garantiaVencendo(asset.warrantyExpiresAt) ? 'text-red-600 font-medium' : 'text-gray-900';

  async function handleDelete() {
    setConfirmDelete(false);
    try {
      await excluir.mutateAsync(id);
      navigate('/ativos');
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } }).response?.data?.message
        ?? 'Falha ao excluir ativo';
      setFeedback({ type: 'error', text: typeof message === 'string' ? message : 'Falha ao excluir ativo' });
    }
  }

  async function handleDownloadDoc(docId: string) {
    try {
      await downloadMutation.mutateAsync(docId);
    } catch {
      setFeedback({ type: 'error', text: 'Falha ao baixar documento' });
    }
  }

  const isMaint = asset.status === 'in_maintenance';

  return (
    <PageContainer
      title="Gestão de Ativos"
      breadcrumbs={[{ label: 'Compras' }, { label: 'Ativos', to: '/ativos' }, { label: displayCode }]}
      action={
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="secondary" size="sm" icon={<PrinterIcon className="h-4 w-4" />} onClick={() => window.print()}>
            Imprimir
          </Button>
          {asset.status !== 'retired' && canEdit && (
            <Button size="sm" icon={<Edit className="h-4 w-4" />} onClick={() => setEditOpen(true)}>
              Editar Ativo
            </Button>
          )}
          {asset.status !== 'retired' && canAssign && asset.status === 'available' && (
            <>
              <Button
                size="sm"
                icon={<UserPlus className="h-4 w-4" />}
                onClick={() => setEntregaOpen(true)}
                className="!bg-green-600 hover:!bg-green-700 focus:!ring-green-500"
              >
                Entregar ativo
              </Button>
              <Button
                variant="secondary"
                size="sm"
                icon={<UserPlus className="h-4 w-4" />}
                onClick={() => setLoanOpen(true)}
                className="!border-yellow-300 !text-yellow-800 hover:!bg-yellow-50"
              >
                Emprestar
              </Button>
            </>
          )}
          {asset.status !== 'retired' && canAssign && (asset.status === 'in_use' || asset.status === 'on_loan') && (
            <>
              <Button
                variant="secondary"
                size="sm"
                icon={<ArrowRightLeft className="h-4 w-4" />}
                onClick={() => setTransferOpen(true)}
                className="!border-blue-300 !text-blue-700 hover:!bg-blue-50"
              >
                Transferir
              </Button>
              <Button
                variant="secondary"
                size="sm"
                icon={<UserMinus className="h-4 w-4" />}
                onClick={() => setDevolucaoOpen(true)}
                className="!border-orange-300 !text-orange-700 hover:!bg-orange-50"
              >
                Registrar devolução
              </Button>
              <Button
                variant="secondary"
                size="sm"
                icon={<Repeat className="h-4 w-4" />}
                onClick={() => setReplaceOpen(true)}
                className="!border-indigo-300 !text-indigo-700 hover:!bg-indigo-50"
              >
                Substituir
              </Button>
            </>
          )}
          {asset.status !== 'retired' && canAssign && isMaint && (
            <Button
              variant="secondary"
              size="sm"
              icon={<UserPlus className="h-4 w-4" />}
              disabled
              title="Ativo em manutenção — não pode ser vinculado (RN007)"
            >
              Entregar (em manutenção)
            </Button>
          )}
          {asset.status !== 'retired' && canMaintain && !isMaint && (
            <Button
              variant="secondary"
              size="sm"
              icon={<Wrench className="h-4 w-4" />}
              onClick={() => goToTab('maintenance')}
              className="!border-purple-300 !text-purple-700 hover:!bg-purple-50"
            >
              Manutenção
            </Button>
          )}
          {asset.status !== 'retired' && canRetire && (
            <Button
              variant="secondary"
              size="sm"
              icon={<Archive className="h-4 w-4" />}
              onClick={() => setRetireOpen(true)}
              className="!border-red-300 !text-red-700 hover:!bg-red-50"
            >
              Baixa patrimonial
            </Button>
          )}
          {asset.status !== 'retired' && canDelete && (
            <Button
              variant="danger"
              size="sm"
              icon={<Trash2 className="h-4 w-4" />}
              onClick={() => setConfirmDelete(true)}
              className="!bg-white !text-red-600 !border !border-red-300 hover:!bg-red-50"
            >
              Excluir
            </Button>
          )}
        </div>
      }
    >
      {feedback && <Alert variant={feedback.type}>{feedback.text}</Alert>}

      <ConfirmDialog
        open={confirmDelete}
        title="Excluir ativo"
        message={`Confirmar exclusão de ${displayCode}? Movimentações e vínculos históricos são preservados; o ativo deixa de aparecer na listagem.`}
        confirmLabel="Excluir"
        cancelLabel="Cancelar"
        danger
        busy={excluir.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          {/* Card identificação */}
          <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="rounded-lg bg-blue-50 p-3">
                  <CategoryIcon group={d.category?.group} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="font-mono text-lg font-semibold text-gray-900">{displayCode}</h2>
                    <AtivoStatusBadge status={asset.status} />
                  </div>
                  <p className="mt-1 text-gray-700">{asset.description}</p>
                  <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                    <div><dt className="inline text-gray-500">Categoria:</dt> <dd className="inline text-gray-900">{d.category?.name ?? '—'}</dd></div>
                    <div><dt className="inline text-gray-500">Marca/Modelo:</dt> <dd className="inline text-gray-900">{[asset.brand, asset.model].filter(Boolean).join(' ') || '—'}</dd></div>
                    <div><dt className="inline text-gray-500">Série:</dt> <dd className="inline text-gray-900">{asset.serialNumber ?? '—'}</dd></div>
                    <div><dt className="inline text-gray-500">Código de barras:</dt> <dd className="inline font-mono text-gray-900">{asset.barcode ?? '—'}</dd></div>
                    <div><dt className="inline text-gray-500">Cód. patrimonial:</dt> <dd className="inline font-mono text-gray-900">{asset.assetCode}</dd></div>
                  </dl>
                </div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-white p-2">
                <QRCodeSVG value={qrValue} size={96} />
                <p className="mt-1 text-center text-[10px] text-gray-500">QR Code</p>
              </div>
            </div>
          </section>

          {/* Card Informações Gerais */}
          <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-gray-500">Informações Gerais</h3>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              <div className="space-y-2 text-sm">
                <p><span className="text-gray-500">Fornecedor: </span><span className="text-gray-900">{d.supplier?.tradeName ?? d.supplier?.corporateName ?? '—'}</span></p>
                <p><span className="text-gray-500">Data da compra: </span><span className="text-gray-900">{formatarData(asset.purchaseDate)}</span></p>
                <p><span className="text-gray-500">Valor de aquisição: </span><span className="text-gray-900">{formatarMoeda(asset.purchaseValue)}</span></p>
                <p><span className="text-gray-500">Nota fiscal: </span><span className="text-gray-900">{asset.invoiceNumber ?? '—'}</span></p>
                <p><span className="text-gray-500">Garantia: </span><span className="text-gray-900">{asset.warrantyMonths ? `${asset.warrantyMonths} meses` : '—'}</span></p>
                <p><span className="text-gray-500">Vencimento da garantia: </span><span className={warrantyClass}>{formatarData(asset.warrantyExpiresAt)}</span></p>
              </div>
              <div className="space-y-2 text-sm">
                <p><span className="text-gray-500">Categoria: </span><span className="text-gray-900">{d.category?.name ?? '—'}</span></p>
                <p><span className="text-gray-500">Marca/Modelo: </span><span className="text-gray-900">{[asset.brand, asset.model].filter(Boolean).join(' ') || '—'}</span></p>
                {Object.entries(asset.specs ?? {}).map(([k, v]) => (
                  <p key={k}>
                    <span className="text-gray-500">{SPEC_LABELS[k] ?? k}: </span>
                    <span className="text-gray-900">
                      {typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v)}
                    </span>
                  </p>
                ))}
              </div>
              <div className="space-y-2 text-sm">
                <p>
                  <span className="text-gray-500">Localização atual: </span>
                  <span className="text-gray-900">{d.location?.name ?? '—'}</span>
                  {d.location?.type && (
                    <span className="ml-1 text-xs text-gray-500">
                      ({locationTypeLabel(d.location.type)}
                      {d.location.clientName ? ` · ${d.location.clientName}` : ''})
                    </span>
                  )}
                </p>
                {[
                  ['Prédio', d.detailedLocation?.building],
                  ['Andar', d.detailedLocation?.floor],
                  ['Sala', d.detailedLocation?.room],
                  ['Corredor', d.detailedLocation?.aisle],
                  ['Prateleira', d.detailedLocation?.shelf],
                  ['Posição', d.detailedLocation?.position],
                ].filter(([, value]) => value).map(([label, value]) => (
                  <p key={label}><span className="text-gray-500">{label}: </span><span className="text-gray-900">{value}</span></p>
                ))}
                <p><span className="text-gray-500">Responsável atual: </span><span className="text-gray-900">{d.currentCustodian?.name ?? '—'}{d.currentCustodian?.jobTitle ? ` (${d.currentCustodian.jobTitle})` : ''}</span></p>
                <p><span className="text-gray-500">Observações: </span><span className="text-gray-900">{asset.notes ?? '—'}</span></p>
              </div>
            </div>
          </section>

          {/* Tabs */}
          <section
            ref={tabsRef}
            className="rounded-lg border border-gray-200 bg-white shadow-sm scroll-mt-4"
          >
            <div className="flex border-b border-gray-200 overflow-x-auto">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`whitespace-nowrap px-4 py-3 text-sm font-medium ${
                    tab === t.key
                      ? 'border-b-2 border-blue-600 text-blue-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <div className="p-4">
              {tab === 'movements' && <MovimentacoesTab assetId={id} />}
              {tab === 'maintenance' && (
                <ManutencaoTab assetId={id} canWrite={canMaintain} assetStatus={asset.status} />
              )}
              {tab === 'attachments' && <AtivoAnexosTab assetId={id} canWrite={canEdit && asset.status !== 'retired'} />}
              {tab === 'licenses' && (
                <LicencasTab
                  assetId={id}
                  currentCustodianId={d.currentCustodian?.id ?? null}
                  isSoftware={asset.assetType === 'software'}
                />
              )}
              {tab === 'inventory' && <InventarioTab assetId={id} />}
            </div>
          </section>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <h4 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Situação do Ativo</h4>
            <div className="flex items-center gap-3">
              <AtivoStatusBadge status={asset.status} />
              <span className="text-sm text-gray-700">{asset.assetType === 'software' ? 'Software' : 'Físico'}</span>
            </div>
            <dl className="mt-4 space-y-1 text-sm">
              <div className="flex justify-between"><dt className="text-gray-500">Aquisição</dt><dd className="text-gray-900">{formatarData(asset.purchaseDate)}</dd></div>
              <div className="flex justify-between"><dt className="text-gray-500">Valor</dt><dd className="text-gray-900">{formatarMoeda(asset.purchaseValue)}</dd></div>
              <div className="flex justify-between"><dt className="text-gray-500">Centro de custo</dt><dd className="text-gray-900">{d.costCenter?.name ?? '—'}</dd></div>
              <div className="flex justify-between"><dt className="text-gray-500">Projeto</dt><dd className="text-gray-900">{d.project?.name ?? '—'}</dd></div>
            </dl>
          </section>

          <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <h4 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Responsável Atual</h4>
            {d.currentCustodian ? (
              <div className="space-y-1 text-sm">
                <div className="flex items-center gap-2">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-100">
                    <UserIcon className="h-5 w-5 text-gray-500" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{d.currentCustodian.name}</p>
                    <p className="text-xs text-gray-500">{d.currentCustodian.jobTitle ?? d.currentCustodian.type}</p>
                  </div>
                </div>
                {d.currentCustodian.email && <p className="text-xs text-gray-600">{d.currentCustodian.email}</p>}
                {d.currentCustodian.phone && <p className="text-xs text-gray-600">{d.currentCustodian.phone}</p>}
                {d.currentCustodian.assignedAt && (
                  <p className="text-xs text-gray-500">Desde: {formatarData(d.currentCustodian.assignedAt)}</p>
                )}

                {/* SPEC 02: banner de assinatura pendente + botão registrar */}
                {(() => {
                  const activeAssignment = assignmentsQuery.data?.data.find((a) => a.returnedAt === null);
                  if (!activeAssignment) return null;
                  if (activeAssignment.signatureStatus === 'pending') {
                    return (
                      <div className="mt-3 rounded border border-yellow-200 bg-yellow-50 p-2">
                        <p className="text-xs font-medium text-yellow-900">
                          Termo {activeAssignment.termNumber} pendente de assinatura
                        </p>
                        <div className="mt-1 flex gap-2">
                          {activeAssignment.responsibilityTermId && (
                            <button
                              onClick={() => handleDownloadDoc(activeAssignment.responsibilityTermId as string)}
                              className="inline-flex items-center gap-1 text-[11px] text-blue-700 hover:underline"
                            >
                              <FileText className="h-3 w-3" /> Baixar termo
                            </button>
                          )}
                          {canEdit && (
                            <button
                              disabled={assinarMutation.isPending}
                              onClick={async () => {
                                try {
                                  await assinarMutation.mutateAsync(activeAssignment.id);
                                } catch (err: unknown) {
                                  const msg = (err as { response?: { data?: { message?: string } } }).response?.data?.message;
                                  setFeedback({ type: 'error', text: typeof msg === 'string' ? msg : 'Falha ao registrar assinatura' });
                                }
                              }}
                              className="inline-flex items-center gap-1 rounded bg-yellow-700 px-2 py-0.5 text-[11px] text-white hover:bg-yellow-800 disabled:opacity-50"
                            >
                              <ClipboardCheck className="h-3 w-3" />
                              {assinarMutation.isPending ? 'Registrando...' : 'Registrar assinatura'}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  }
                  if (activeAssignment.signatureStatus === 'signed') {
                    const behalfOf = activeAssignment.signedOnBehalfOfCustodian;
                    const signer = activeAssignment.signedByUser;
                    return (
                      <div className="mt-3 rounded border border-green-200 bg-green-50 p-2">
                        <p className="text-xs font-medium text-green-900">
                          Termo {activeAssignment.termNumber} · Aceite registrado
                        </p>
                        {(behalfOf || signer) && (
                          <p className="mt-1 text-[11px] text-green-800">
                            {behalfOf
                              ? `Por ${signer?.name ?? 'operador'} em nome de ${behalfOf.name}`
                              : `Por ${signer?.name ?? 'operador'}`}
                            {activeAssignment.signedAt ? ` em ${formatarData(activeAssignment.signedAt)}` : ''}
                          </p>
                        )}
                        {activeAssignment.responsibilityTermId && (
                          <button
                            onClick={() => handleDownloadDoc(activeAssignment.responsibilityTermId as string)}
                            className="mt-1 inline-flex items-center gap-1 text-[11px] text-blue-700 hover:underline"
                          >
                            <FileText className="h-3 w-3" /> Baixar termo
                          </button>
                        )}
                      </div>
                    );
                  }
                  return null;
                })()}

                <button
                  onClick={() => setHistoricoOpen(true)}
                  className="mt-2 inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                >
                  <History className="h-3 w-3" /> Ver histórico de responsáveis
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="flex items-center gap-2 text-sm text-gray-500">
                  <MapPin className="h-4 w-4" /> Nenhum responsável designado.
                </p>
                <button
                  onClick={() => setHistoricoOpen(true)}
                  className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                >
                  <History className="h-3 w-3" /> Ver histórico de responsáveis
                </button>
              </div>
            )}
          </section>

          <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <h4 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Alertas e Pendências</h4>
            <CardAlertas alerts={d.alerts} />
          </section>

          <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <h4 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Documentos do Ativo</h4>
            {d.documents.length === 0 ? (
              <p className="flex items-center gap-2 text-sm text-gray-500">
                <Wallet className="h-4 w-4" /> Nenhum documento enviado.
              </p>
            ) : (
              <ul className="space-y-2">
                {d.documents.slice(0, 5).map((doc) => (
                  <li key={doc.id} className="flex items-start justify-between gap-2 text-xs">
                    <div className="flex min-w-0 items-start gap-1.5">
                      <FileText className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                      <div className="min-w-0">
                        <p className="truncate text-gray-700">{doc.fileName}</p>
                        <p className="text-[11px] text-gray-500">{formatarTamanho(doc.fileSize)}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDownloadDoc(doc.id)}
                      className="flex-shrink-0 rounded p-1 text-gray-500 hover:bg-gray-100"
                    >
                      <Download className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <button
              onClick={() => setTab('attachments')}
              className="mt-2 text-xs text-blue-600 hover:underline"
            >
              Ver todos os documentos
            </button>
          </section>
        </div>
      </div>

      {/* SPEC 02: modais + drawer */}
      <EntregaModal
        open={entregaOpen}
        assetId={id}
        onClose={() => setEntregaOpen(false)}
        onSuccess={(assignment) => {
          // Toast simples via state — o repo ainda não tem sistema de
          // toast, então uso banner. Limpo via useEffect quando a
          // assinatura é registrada.
          setFeedback({
            type: 'success',
            text: `Entrega registrada. Termo ${assignment.termNumber} pendente de assinatura.`,
          });
        }}
      />
      <DevolucaoModal
        open={devolucaoOpen}
        assetId={id}
        assignmentId={assignmentsQuery.data?.data.find((a) => a.returnedAt === null)?.id ?? null}
        onClose={() => setDevolucaoOpen(false)}
        onSuccess={() =>
          setFeedback({ type: 'success', text: 'Devolução registrada. Ativo disponível novamente.' })
        }
      />
      <TransferenciaModal
        open={transferOpen}
        assetId={id}
        onClose={() => setTransferOpen(false)}
        onSuccess={(assignment) => {
          setFeedback({
            type: 'success',
            text: `Transferência registrada. Novo termo ${assignment.termNumber} pendente de assinatura.`,
          });
        }}
      />
      <HistoricoResponsaveisDrawer
        open={historicoOpen}
        assetId={id}
        onClose={() => setHistoricoOpen(false)}
      />

      {/* SPEC 03 modais */}
      <LoanModal
        open={loanOpen}
        assetId={id}
        onClose={() => setLoanOpen(false)}
        onSuccess={(assignment) => {
          setFeedback({
            type: 'success',
            text: `Empréstimo registrado. Termo ${assignment.termNumber} pendente de assinatura.`,
          });
        }}
      />
      <ReplaceModal
        open={replaceOpen}
        assetId={id}
        onClose={() => setReplaceOpen(false)}
        onSuccess={() => setFeedback({ type: 'success', text: 'Substituição registrada com sucesso.' })}
      />
      <RetireModal
        open={retireOpen}
        assetId={id}
        onClose={() => setRetireOpen(false)}
        onSuccess={() => setFeedback({ type: 'success', text: 'Baixa patrimonial registrada.' })}
      />

      <AtivoFormModal
        open={editOpen}
        initial={d}
        onClose={() => setEditOpen(false)}
        onSuccess={() => { setEditOpen(false); detail.refetch(); }}
      />
    </PageContainer>
  );
}
